import type * as THREE from 'three'
import { PlyMeshValidationError } from '@/plugins/mesh/errors'

type PlyFormat = 'ascii' | 'binary_little_endian' | 'binary_big_endian'

interface PlyHeaderProperty {
  readonly name: string
  readonly isList: boolean
}

interface PlyHeaderElement {
  readonly name: string
  readonly count: number
  readonly properties: PlyHeaderProperty[]
}

/** PLY Mesh Header 校验后供解析结果复核使用的最小元数据。 */
export interface PlyMeshHeader {
  readonly format: PlyFormat
  readonly vertexCount: number
  readonly faceCount: number
  readonly allowsNonIndexedGeometry: boolean
}

const SUPPORTED_FORMATS = new Set<PlyFormat>(['ascii', 'binary_little_endian', 'binary_big_endian'])
const FACE_INDEX_PROPERTY_NAMES = new Set(['vertex_indices', 'vertex_index'])
const GSPLAT_PROPERTY_NAMES = [
  'opacity',
  'scale_0',
  'scale_1',
  'scale_2',
  'rot_0',
  'rot_1',
  'rot_2',
  'rot_3',
  'f_dc_0',
  'f_dc_1',
  'f_dc_2',
] as const
const FACE_COLOR_PROPERTY_GROUPS = [
  ['red', 'diffuse_red', 'r', 'diffuse_r'],
  ['green', 'diffuse_green', 'g', 'diffuse_g'],
  ['blue', 'diffuse_blue', 'b', 'diffuse_b'],
] as const

function invalidHeader(source: string, detail: string): never {
  throw new PlyMeshValidationError(source, 'invalid-header', detail)
}

function findTextHeaderEnd(data: string): number {
  let lineStart = 0
  while (lineStart <= data.length) {
    let lineEnd = lineStart
    while (lineEnd < data.length && data[lineEnd] !== '\r' && data[lineEnd] !== '\n') {
      lineEnd += 1
    }

    if (data.slice(lineStart, lineEnd).trim() === 'end_header') {
      if (lineEnd >= data.length) return lineEnd
      const firstBreak = data[lineEnd]
      lineEnd += 1
      if (firstBreak === '\r' && data[lineEnd] === '\n') lineEnd += 1
      return lineEnd
    }

    if (lineEnd >= data.length) break
    if (data[lineEnd] === '\r' && data[lineEnd + 1] === '\n') lineEnd += 1
    lineStart = lineEnd + 1
  }
  return -1
}

function isWhitespaceByte(value: number): boolean {
  return value === 0x09 || value === 0x20
}

function isEndHeaderLine(bytes: Uint8Array, start: number, end: number): boolean {
  while (start < end && isWhitespaceByte(bytes[start])) start += 1
  while (end > start && isWhitespaceByte(bytes[end - 1])) end -= 1
  const marker = 'end_header'
  if (end - start !== marker.length) return false
  for (let index = 0; index < marker.length; index += 1) {
    if (bytes[start + index] !== marker.charCodeAt(index)) return false
  }
  return true
}

function findBinaryHeaderEnd(bytes: Uint8Array): number {
  let lineStart = 0
  while (lineStart <= bytes.byteLength) {
    let lineEnd = lineStart
    while (lineEnd < bytes.byteLength && bytes[lineEnd] !== 0x0d && bytes[lineEnd] !== 0x0a) {
      lineEnd += 1
    }

    if (isEndHeaderLine(bytes, lineStart, lineEnd)) {
      if (lineEnd >= bytes.byteLength) return lineEnd
      const firstBreak = bytes[lineEnd]
      lineEnd += 1
      if (firstBreak === 0x0d && bytes[lineEnd] === 0x0a) lineEnd += 1
      return lineEnd
    }

    if (lineEnd >= bytes.byteLength) break
    if (bytes[lineEnd] === 0x0d && bytes[lineEnd + 1] === 0x0a) lineEnd += 1
    lineStart = lineEnd + 1
  }
  return -1
}

function extractPlyHeaderText(data: ArrayBuffer | string, source: string): string {
  if (typeof data === 'string') {
    const headerEnd = findTextHeaderEnd(data)
    if (headerEnd < 0) invalidHeader(source, '缺少 end_header')
    return data.slice(0, headerEnd)
  }

  const bytes = new Uint8Array(data)
  const headerEnd = findBinaryHeaderEnd(bytes)
  if (headerEnd < 0) invalidHeader(source, '缺少 end_header')
  return new TextDecoder().decode(bytes.subarray(0, headerEnd))
}

function parseElementCount(value: string | undefined, source: string, elementName: string): number {
  const count = Number(value)
  if (!Number.isSafeInteger(count) || count < 0) {
    invalidHeader(source, `element ${elementName} 的数量无效`)
  }
  return count
}

function parsePlyHeader(
  data: ArrayBuffer | string,
  source: string
): {
  format: PlyFormat
  elements: PlyHeaderElement[]
} {
  const lines = extractPlyHeaderText(data, source).split(/\r\n|\n|\r/)
  if (lines[0]?.trim() !== 'ply') invalidHeader(source, '缺少 ply 文件标识')

  let format: PlyFormat | undefined
  let currentElement: PlyHeaderElement | undefined
  const elements: PlyHeaderElement[] = []

  for (const rawLine of lines.slice(1)) {
    const line = rawLine.trim()
    if (!line || line === 'end_header' || line.startsWith('comment ')) continue
    const values = line.split(/\s+/)
    const keyword = values[0]

    if (keyword === 'format') {
      const candidate = values[1] as PlyFormat | undefined
      if (!candidate || !SUPPORTED_FORMATS.has(candidate)) {
        invalidHeader(source, `不支持的 format: ${candidate ?? '(缺失)'}`)
      }
      format = candidate
      continue
    }

    if (keyword === 'element') {
      const name = values[1]
      if (!name) invalidHeader(source, 'element 缺少名称')
      currentElement = {
        name,
        count: parseElementCount(values[2], source, name),
        properties: [],
      }
      elements.push(currentElement)
      continue
    }

    if (keyword === 'property') {
      if (!currentElement) invalidHeader(source, 'property 未归属任何 element')
      const isList = values[1] === 'list'
      const name = isList ? values[4] : values[2]
      if (!name) invalidHeader(source, `${currentElement.name} property 声明不完整`)
      currentElement.properties.push({ name, isList })
    }
  }

  if (!format) invalidHeader(source, '缺少 format 声明')
  return { format, elements }
}

function isGaussianSplatVertex(element: PlyHeaderElement): boolean {
  const names = new Set(element.properties.map((property) => property.name.toLowerCase()))
  return GSPLAT_PROPERTY_NAMES.every((name) => names.has(name))
}

function hasFaceVertexAttributes(element: PlyHeaderElement): boolean {
  const properties = new Map(
    element.properties.map((property) => [property.name.toLowerCase(), property.isList])
  )
  if (properties.get('texcoord') === true) return true
  return FACE_COLOR_PROPERTY_GROUPS.every((aliases) => aliases.some((name) => properties.has(name)))
}

/** 校验 PLY Header 确实声明了 Three.js 可解析的静态 Mesh 面拓扑。 */
export function validatePlyMeshHeader(data: ArrayBuffer | string, source: string): PlyMeshHeader {
  const header = parsePlyHeader(data, source)
  const vertex = header.elements.find((element) => element.name === 'vertex')
  if (!vertex || vertex.count === 0) {
    throw new PlyMeshValidationError(source, 'missing-vertices', '未声明非空 vertex 元素')
  }

  const face = header.elements.find((element) => element.name === 'face')
  if (!face || face.count === 0) {
    const code = isGaussianSplatVertex(vertex) ? 'gaussian-splat' : 'point-data-only'
    const detail = code === 'gaussian-splat' ? '检测到无面的 3DGS PLY' : 'PLY 只包含点数据，没有面'
    throw new PlyMeshValidationError(source, code, detail)
  }

  const hasFaceIndices = face.properties.some(
    (property) => property.isList && FACE_INDEX_PROPERTY_NAMES.has(property.name)
  )
  if (!hasFaceIndices) {
    throw new PlyMeshValidationError(
      source,
      'missing-face-indices',
      'face 缺少 vertex_indices 或 vertex_index list 属性'
    )
  }

  return {
    format: header.format,
    vertexCount: vertex.count,
    faceCount: face.count,
    allowsNonIndexedGeometry: hasFaceVertexAttributes(face),
  }
}

function invalidTopology(source: string, detail: string): never {
  throw new PlyMeshValidationError(source, 'invalid-topology', detail)
}

function validateFinitePositions(
  position: THREE.BufferAttribute | THREE.InterleavedBufferAttribute,
  source: string
): void {
  if (position.itemSize < 3) invalidTopology(source, 'position 属性维度不足 3')
  for (let index = 0; index < position.count; index += 1) {
    if (
      !Number.isFinite(position.getX(index)) ||
      !Number.isFinite(position.getY(index)) ||
      !Number.isFinite(position.getZ(index))
    ) {
      invalidTopology(source, `顶点 ${index} 包含非有限坐标`)
    }
  }
}

/** 复核 Three.js 解析后的 Geometry 与 Header 声明能够形成有效三角面。 */
export function validatePlyMeshGeometry(
  geometry: THREE.BufferGeometry,
  header: PlyMeshHeader,
  source: string
): void {
  const position = geometry.getAttribute('position')
  if (!position || position.count < 3) invalidTopology(source, '可渲染顶点不足 3 个')
  validateFinitePositions(position, source)

  const index = geometry.getIndex()
  const drawCount = index?.count ?? position.count
  if (drawCount < 3 || drawCount % 3 !== 0) {
    invalidTopology(source, `绘制元素数量 ${drawCount} 不能组成完整三角形`)
  }

  const triangleCount = drawCount / 3
  if (triangleCount < header.faceCount || triangleCount > header.faceCount * 2) {
    invalidTopology(
      source,
      `解析得到 ${triangleCount} 个三角形，与 ${header.faceCount} 个 face 声明不一致`
    )
  }

  if (!index) {
    if (!header.allowsNonIndexedGeometry) {
      invalidTopology(source, 'face 未生成三角形索引')
    }
    const faceAttribute = geometry.getAttribute('uv') ?? geometry.getAttribute('color')
    if (!faceAttribute || faceAttribute.count !== position.count) {
      invalidTopology(source, '非索引 Geometry 缺少完整的面顶点属性')
    }
    return
  }

  if (index.itemSize !== 1) invalidTopology(source, '三角形索引 itemSize 必须为 1')
  for (let offset = 0; offset < index.count; offset += 1) {
    const vertexIndex = index.getX(offset)
    if (!Number.isInteger(vertexIndex) || vertexIndex < 0 || vertexIndex >= position.count) {
      invalidTopology(source, `索引 ${vertexIndex} 越过顶点范围 0..${position.count - 1}`)
    }
  }
}
